<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="locator">
    <form
      :action="locatorBaseUrl"
      method="GET"
    >
      <kf-heading
        v-if="i18n.locator.title"
        tag="h1"
        class="locator-title"
      >
        {{ i18n.locator.title }}
      </kf-heading>
      <fieldset>
        <legend class="locator-legend">
          <strong v-if="!hasMultiChildCareTypes">{{ childcareTypes[0].childcareTypeName }}</strong>
        </legend>
        <div class="radio-button-list-wrapper">
          <div
            v-show="hasMultiChildCareTypes"
            class="radio-button-list mb-2"
          >
            <div
              v-for="(childcareType, index) in childcareTypes"
              :key="index"
              class="radio-button-list-item"
            >
              <span class="hidden lg:block text-sm mb-1">{{ childcareType.childcareTypeName === 'BSO' || childcareType.childcareTypeName === 'Bso' ? i18n.locator.bsoLabel.label : childcareType.childcareTypeName }}</span>
              <input
                :id="`childcaretype-${index}`"
                v-model="activeChildcareType"
                type="radio"
                name="locator-type"
                :value="childcareType.childcareTypeKey"
                class="radio-button-input"
              >
              <label
                :for="`childcaretype-${index}`"
                class="radio-button-label"
              >
                <span class="inline-block lg:hidden ml-2 text-sm">{{ childcareType.childcareTypeName === 'BSO' || childcareType.childcareTypeName === 'Bso' ? i18n.locator.bsoLabel.label : childcareType.childcareTypeName }}</span>
                <span class="mx-2 rounded-md text-sm bg-gray-50 py-1 px-2 whitespace-nowrap">{{ childcareType.childcareTypeSubTitle }}</span>
              </label>
            </div>
          </div>

          <div
            class="type-1"
          >
            <div class="zipcode-container">
              <div class="zipcode-input-wrapper w-full relative">
                <input
                  id="locator-postalcode-input"
                  v-model="locatorForm.postalcode"
                  name="postal-code"
                  type="text"
                  :placeholder="i18n.locator.postalcodeInput.placeholder"
                  :aria-label="i18n.locator.postalcodeInput.placeholder"
                  value=""
                  class="form-control w-full max-w-full"
                  @input="sanitizeLocatorInput"
                >
                <kf-button
                  type="primary"
                  :disabled="!((activeChildcareType !== null && forcePostalCode) ||
                    activeChildcareType === 'Daycare' ||
                    activeChildcareType === 'Preschool')"
                  class="btn absolute top-0 right-0 min-w-0 w-10 h-10"
                  @click="trackGtmEvents"
                >
                  <ArrowRight class="icon" />
                </kf-button>

                <LocatorUserLocation
                  v-model="locatorForm.postalcode"
                  class="absolute top-4 left-4"
                />
              </div>
            </div>
          </div>

          <loader
            v-if="isLoading"
            loader-color="primary"
          />
          <div
            v-if="!forcePostalCode && activeChildcareType === 'After school care'"
            class="type-2"
          >
            <div v-if="!schoolsByCity.errorLoading">
              <div class="locator-select-wrapper">
                <label for="city">
                  <span class="locator-select-label">{{ i18n.locator.city.label }}</span>
                  <kf-fields-auto-suggest
                    v-if="schoolsByCity && schoolsByCity.data && schoolsByCity.data.cities"
                    id="city"
                    name="city"
                    class="locator-select form-select"
                    :disabled="!schoolsByCity.data"
                    :initial-value="initialFormData.city"
                    :placeholder="i18n.locator.select.optionBase.label"
                    :suggestion-list="schoolsByCity.data.cities"
                    @select-suggestion="handleCitySelect($event)"
                    @reset-suggestion="handleCitySelect(null)"
                  />
                </label>
              </div>

              <div
                v-if="selectedCity && !isWhyPlan"
                class="locator-select-wrapper"
              >
                <label for="school">
                  <span class="locator-select-label">{{ i18n.locator.school.label }}</span>
                  <kf-fields-auto-suggest
                    v-if="schoolsByCity && schoolsByCity.data && schoolsByCity.data.cities"
                    id="school"
                    ref="school-select"
                    name="school"
                    class="locator-select form-select"
                    :disabled="!schoolsByCity.data"
                    :initial-value="initialFormData.school"
                    :placeholder="i18n.locator.select.optionBase.label"
                    :suggestion-list="selectedCity.schools"
                    @select-suggestion="handleSchoolSelect($event)"
                    @reset-suggestion="handleSchoolSelect(null)"
                  />
                </label>
              </div>

              <kf-button
                v-if="locatorForm"
                class="btn"
                type="primary"
                :disabled="!canSubmit"
                @click="trackGtmEvents"
              >
                {{ i18n.locator.findPartouButton.label }}
              </kf-button>

              <!-- eslint-disable  vue/no-v-text-v-html-on-component -->
              <kf-rich-text
                v-if="selectedCity"
                class="radio-button-list-helper-text"
                v-html="locatorHelperText"
              />
              <!-- eslint-enable vue/no-v-text-v-html-on-component -->
            </div>
          </div>

          <div
            v-if="schoolsByCity.errorLoading"
            class="text-danger"
          >
            {{ i18n.locator.error.retrievingSchools }}
          </div>

          <input
            type="hidden"
            name="scroll-to-result"
            value="1"
          >
        </div>
      </fieldset>
    </form>
  </div>
</template>

<script>
import KfButton from '@/components/base/button/Button';
import KfHeading from '@/components/base/heading/Heading.vue';
import KfRichText from '@/components/base/rich-text/RichText.vue';
import KfFieldsAutoSuggest from '@/components/forms/fields/FieldsAutoSuggest.vue';
import LocatorUserLocation from '@/components/locator/LocatorUserLocation.vue';
import ArrowRight from '@/components/svg/ArrowRight.vue';
import { setDayCareString, trackEvent } from '@/lib/gtm/trackEvent';
import axios from 'axios';
import Loader from '../loader/Loader.vue';

export default {
  name: 'KfLocator',
  components: {
    ArrowRight,
    KfButton,
    KfRichText,
    KfHeading,
    KfFieldsAutoSuggest,
    LocatorUserLocation,
    Loader,
  },
  props: {
    i18n: {
      type: Object,
      default: () => {},
    },
    childcareTypes: {
      type: Array,
      default: () => [],
    },
    initialFormData: {
      type: Object,
      default: () => {},
    },
    locatorBaseUrl: {
      type: String,
      default: '',
    },
    locatorHelperText: {
      type: String,
      default: '',
    },
    apiEndpointForRetrievingSchools: {
      type: String,
      default: '/umbraco/api/Content/GetSchools',
    },
    forcePostalCode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeChildcareType: null,
      schoolsByCity: {
        isLoading: false,
        errorLoading: false,
        data: null,
      },
      childcareSchools: null,
      selectedCity: null,
      isWhyPlan: false,
      canSubmit: false,
      locatorForm: {
        city: '',
        school: '',
        postalcode: '',
      },
      trackEvent,
    };
  },
  computed: {
    hasMultiChildCareTypes() {
      return this.childcareTypes.length > 1;
    },
    trackData() {
      const {
        activeChildcareType,
        locatorForm: { city, school, postalcode },
      } = this;

      let data = {
        event: 'childcare_type',
        event_name: 'childcare_type',
        event_category: 'registration',
        event_action: 'chose_childcare_type',
        event_label: activeChildcareType,
        location: activeChildcareType === 'After school care' ? city : postalcode,
      };
      if (school !== '') {
        data = {
          ...data,
          school,
        };
      }
      return data;
    },
  },
  created() {
    this.isLoading = true;
    if (this.initialFormData?.city) {
      this.handleCitySelect(this.initialFormData.city);
    }
    if (this.initialFormData?.school) {
      this.handleSchoolSelect(this.initialFormData.school);
    }
    this.isLoading = false;
  },
  mounted() {
    this.fillInInitialDataForCareTypesAndPostalCodes();
    this.retrieveSchoolsByCity().then(() => {
      this.fillInInitialDataForCitiesAndSchools();
    });

    this.$nextTick(() => {
      // Check if scroll needed. If so, go down...
      this.scrollToContent();
    });
    this.isLoading = false;
  },
  methods: {
    trackGtmEvents() {
      trackEvent(this.trackData, this.$gtm.enabled());
      trackEvent(
        {
          event: 'registration',
          event_name: 'childcareType',
          event_category: 'childcareType',
          event_action: 'chooseChildcareType',
          event_label: 'chooseChildcareType',
          childcare_type: setDayCareString(this.activeChildcareType),
        },
        this.$gtm.enabled(),
      );
    },
    async retrieveSchoolsByCity() {
      this.schoolsByCity.errorLoading = false;
      this.schoolsByCity.isLoading = true;

      const requestVerificationToken = document.body.querySelector(
        'input[type=hidden][name=__RequestVerificationToken]',
      )?.value;

      const config = {
        headers: {
          'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
        },
      };

      return axios
        .get(this.apiEndpointForRetrievingSchools, config)
        .then((response) => {
          this.schoolsByCity.data = response.data;
          this.schoolsByCity.isLoading = false;
        })
        .catch(() => {
          this.schoolsByCity.isLoading = false;
          this.schoolsByCity.errorLoading = true;
        });
    },
    fillInInitialDataForCareTypesAndPostalCodes() {
      if (
        this.childcareTypes.some(
          (type) => type.childcareTypeKey === this.initialFormData.childcareType,
        )
      ) {
        this.activeChildcareType = this.initialFormData.childcareType;
      }

      if (this.initialFormData.postalcode) {
        this.locatorForm.postalcode = this.initialFormData.postalcode;
      }
    },
    fillInInitialDataForCitiesAndSchools() {
      const city = this.schoolsByCity.data?.cities?.find(
        (city) => city.name === this.initialFormData.city,
      );
      if (city && city.schools.some((school) => school.name === this.initialFormData.school)) {
        this.locatorForm.city = this.initialFormData.city;

        this.locatorForm.school = this.initialFormData.school;
      }
    },
    handleCitySelect(city) {
      if (city) {
        this.selectedCity = city;
        this.isWhyPlan = city.isWhyPlanPilot;
        this.canSubmit = this.isWhyPlan;
      } else if (city === null) {
        this.canSubmit = false;
        this.selectedCity = null;
        this.locatorForm = {
          city: '',
          postalcode: '',
          school: '',
        };

        return;
      }

      const selectedSchoolExistsInSelectedCity =
        this.selectedCity &&
        this.selectedCity.schools &&
        !!this.selectedCity.schools.find((item) => {
          if (item?.name === this.locatorForm?.school?.name) {
            return JSON.stringify(item) === JSON.stringify(this.locatorForm.school);
          }

          return false;
        });

      const schoolRef = this.$refs['school-select'];

      if (!selectedSchoolExistsInSelectedCity) {
        this.handleSchoolSelect(null);

        // Reset suggestion if schoolref is available
        if (schoolRef && typeof schoolRef.resetSuggestion === 'function') {
          schoolRef.resetSuggestion();
        }
      }
    },

    /**
     * Simple XSS guard: strip out anything looking like an HTML tag
     */
    sanitizeLocatorInput() {
      this.locatorForm.postalcode = this.locatorForm.postalcode
        .replace(/<[^>]*>/g, '') // remove tags
        .replace(/javascript:/gi, ''); // nix any “javascript:” URIs
    },

    handleSchoolSelect(school) {
      this.locatorForm.school = school;
      school || this.isWhyPlan ? (this.canSubmit = true) : (this.canSubmit = false);
    },

    scrollToContent() {
      if (window.location.search.indexOf('scroll-to-result=1') === -1) {
        return;
      }

      const scrollWrapper = document.querySelector('[data-scroll-results]');

      if (!scrollWrapper) {
        return;
      }

      scrollWrapper.scrollIntoView({ behavior: 'smooth' });
    },
  },
};
</script>

<style lang="postcss">
.locator {
  &-legend,
  &-select-label {
    @apply mb-4 text-base text-black block;
  }

  &-title {
    @apply text-black text-2xl mb-2;
  }

  .radio-button {
    &-container {
      .radio-button-list {
        @apply flex;
      }
    }

    &-list {
      @apply flex flex-col gap-2 mb-4;

      @screen md {
        @apply flex-row gap-4;
      }

      &-wrapper {
        @apply flex flex-col;

        .btn-primary {
          min-width: 0px;
        }

        .zipcode-container {
          @apply flex flex-col;

          @screen md {
            @apply flex-row gap-4;
          }

          .btn-primary {
            @apply p-3 h-[48px] top-1 right-1;
            aspect-ratio: 1/1;
          }
        }

        .zipcode-input-wrapper {
          @apply relative mb-4;

          @screen md {
            @apply mb-0;
          }

          .form-control {
            height: 56px;

            &::placeholder {
              @apply text-gray-100;
            }
          }

          /* &::before {
            @apply block absolute text-primary-alpha z-10 w-5 h-5 bg-no-repeat;
            content: '';
            background-image: url('~@/assets/svg/location.svg?inline');
            top: 17px;
            left: 19px;
          } */
          /*
          .theme-secondary & {
            &::before {
              background-image: url('~@/assets/svg/theme-secondary/ts-location.svg?inline');
            }
          } */
        }

        .form-control {
          @apply relative border border-gray-75 h-full pl-12;

          @screen md {
            /* max-width: 220px; */
          }
        }
      }

      &-item {
        input[type='radio'] {
          opacity: 0;

          + label {
            position: relative;
            cursor: pointer;

            &::before {
              @apply absolute border border-gray-75 bg-white rounded-full top-0;
              width: 21px;
              height: 21px;
              content: '';
              left: -21px;
            }

            &::after {
              @apply absolute rounded-full;
              content: '';
              left: -15px;
              top: 6px;
              width: 9px;
              height: 9px;
            }
          }

          &:checked {
            + label {
              &::after {
                @apply bg-primary-bravo;

                /* The background is removed in Windows high-contrast mode, so we
                  need to set it explicitly here. `WindowText` is a system color
                  that should work with whatever high contrast mode the user
                  has set. */
                @media screen and (-ms-high-contrast: active) {
                  & {
                    background: WindowText;
                  }
                }
              }
            }
          }

          &:focus {
            + label {
              &::before {
                @apply border-black border-2;
              }
            }
          }
        }
      }

      &-helper-text {
        p {
          @apply mt-4 text-sm text-center;

          @screen md {
            @apply text-left;
          }
        }
      }
    }

    &-input {
      @apply mr-2;
    }

    &-label {
      @apply text-base;
    }
  }
}

.form {
  &-control {
    @apply block w-full py-1 px-3 text-base font-normal text-black bg-white border border-gray-75 appearance-none rounded-full;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  }
}
</style>
